/** @format */

import { Injectable, inject } from '@angular/core';
import {
  Scene7MediaSetItem,
  Scene7VideoSetItem,
} from '../interfaces/scene7-media-set-response.interface';

import { Environment } from '@shared/data-access/interfaces';
import { HmfMediaSetItem } from '@dsg-tech/homefield-utils';

@Injectable({
  providedIn: 'root',
})
export class Scene7MediaSetParserService {
  private readonly environment = inject(Environment);

  /**
   * @description
   * Parses Scene7 MediaSet items into HmfMediaSetItem format to be used in the template
   *
   * @param scene7MediaSetItems - Array of Scene7 media set items from API
   * @param fullscreenImageWidths - Optional widths for image srcset generation
   * @returns Array of parsed media set items ready for UI display
   */
  parseMediaSetItems(
    scene7MediaSetItems: Scene7MediaSetItem[],
    fullscreenImageWidths: number[] = [685, 1000, 1500, 2000],
  ): HmfMediaSetItem[] {
    // Return data
    const returnMediaSetItems: HmfMediaSetItem[] = [];

    // Early exit if the Scene7 MediaSet items are empty
    if (Array.isArray(scene7MediaSetItems) && scene7MediaSetItems.length === 0) {
      return returnMediaSetItems;
    }

    // Iterate through the Scene7 MediaSet items
    for (let i = 0, length = scene7MediaSetItems.length; i < length; i += 1) {
      // Get a reference to the current index for convenience and to prevent multiple lookups
      const currentScene7MediaSetItem: Scene7MediaSetItem = scene7MediaSetItems[i];

      // The image file name in Scene7
      let scene7ImageName: string | undefined;

      // Check where to get the image name from.  ImageComingSoon will not have a swatch object and only an image object
      if (typeof currentScene7MediaSetItem.s !== 'undefined') {
        // Use the swatch object
        scene7ImageName = currentScene7MediaSetItem.s.n;
      } else {
        // use the image object
        scene7ImageName = currentScene7MediaSetItem.i?.n;
      }

      // Build a new parsed media set item
      const mediaSetItem: HmfMediaSetItem = this.buildMediaSetItem(
        i,
        scene7ImageName,
        currentScene7MediaSetItem,
        fullscreenImageWidths,
      );

      // Add the media set item to the return array
      returnMediaSetItems.push(mediaSetItem);
    }

    // Return the data
    return returnMediaSetItems;
  }

  /**
   * @description
   * Builds a single HmfMediaSetItem from Scene7 media set item data
   *
   * @param index - Index of the media item in the set
   * @param scene7ImageName - Image name from Scene7
   * @param currentScene7MediaSetItem - Original Scene7 media set item
   * @param fullscreenImageWidths - Optional widths for image srcset generation
   * @returns Processed media set item ready for UI consumption
   */
  buildMediaSetItem(
    index: number,
    scene7ImageName: string | undefined,
    currentScene7MediaSetItem: Scene7MediaSetItem,
    fullscreenImageWidths: number[] = [685, 1000, 1500, 2000],
  ): HmfMediaSetItem {
    let mediaSetItem: HmfMediaSetItem = {
      srcset: '',
      type: '',
      index: index,
    };

    // Set the thumbnail and src properties
    mediaSetItem.thumb = `${this.environment.thirdParty.scene7?.baseConfig?.baseUrl}${this.environment.thirdParty.scene7?.baseConfig?.imageDirectory}${scene7ImageName}?wid=250&hei=250&fmt=pjpeg`;
    mediaSetItem.templateSrc = `${this.environment.thirdParty.scene7?.baseConfig?.baseUrl}${this.environment.thirdParty.scene7?.baseConfig?.imageDirectory}${scene7ImageName}`;

    // Determine which type of asset the current item is
    if (
      typeof currentScene7MediaSetItem.type === 'string' &&
      currentScene7MediaSetItem.type.toLowerCase() === 'video'
    ) {
      mediaSetItem = this.buildVideoMediaSetItem(
        mediaSetItem,
        scene7ImageName,
        currentScene7MediaSetItem,
      );
    } else if (
      typeof currentScene7MediaSetItem.type === 'string' &&
      currentScene7MediaSetItem.type.toLowerCase() === 'video_set'
    ) {
      mediaSetItem = this.buildVideoSetMediaSetItem(
        mediaSetItem,
        scene7ImageName,
        currentScene7MediaSetItem,
      );
    } else {
      mediaSetItem = this.buildImageMediaSetItem(
        mediaSetItem,
        scene7ImageName,
        fullscreenImageWidths,
      );
    }

    return mediaSetItem;
  }

  /**
   * @description
   * Builds a media set item for video type
   *
   * @param mediaSetItem - Media set item to populate
   * @param scene7ImageName - Image name from Scene7
   * @param currentScene7MediaSetItem - Original Scene7 media set item
   * @private
   */
  buildVideoMediaSetItem(
    mediaSetItem: HmfMediaSetItem,
    scene7ImageName: string | undefined,
    currentScene7MediaSetItem: Scene7MediaSetItem,
  ): HmfMediaSetItem {
    const videoMediaSetItem = { ...mediaSetItem };

    // Set the poster
    videoMediaSetItem.poster = `${this.environment.thirdParty.scene7?.baseConfig?.baseUrl}${this.environment.thirdParty.scene7?.baseConfig?.imageDirectory}${scene7ImageName}?wid=1400&fmt=pjpeg`;

    // Set the type
    videoMediaSetItem.type = 'video';

    // Set the video HTML
    // Set the track source to use the first video name
    const closedCaptionTag = `<track label="English" kind="captions" srclang="en-us" src="${this.environment.thirdParty.scene7?.baseConfig?.baseUrl}${this.environment.thirdParty.scene7?.baseConfig?.closedCaption}${currentScene7MediaSetItem.i?.n}_CC.vtt">`;

    videoMediaSetItem.html = `<video crossorigin="anonymous" class="lg-video-object lg-html5" controls preload="auto" autoplay="false">
      <source src="${this.environment.thirdParty.scene7?.baseConfig?.baseUrl}${this.environment.thirdParty.scene7?.baseConfig?.videoDirectory}${currentScene7MediaSetItem.i?.n}" type="video/mp4">${closedCaptionTag}Your browser does not support HTML5 video</video>`;

    return videoMediaSetItem;
  }

  /**
   * @description
   * Builds a media set item for video set type
   *
   * @param mediaSetItem - Media set item to populate
   * @param scene7ImageName - Image name from Scene7
   * @param currentScene7MediaSetItem - Original Scene7 media set item
   * @private
   */
  buildVideoSetMediaSetItem(
    mediaSetItem: HmfMediaSetItem,
    scene7ImageName: string | undefined,
    currentScene7MediaSetItem: Scene7MediaSetItem,
  ): HmfMediaSetItem {
    const videoSetMediaSetItem = { ...mediaSetItem };

    // Set the poster
    videoSetMediaSetItem.poster = `${this.environment.thirdParty.scene7?.baseConfig?.baseUrl}${this.environment.thirdParty.scene7?.baseConfig?.imageDirectory}${scene7ImageName}?wid=1400&fmt=pjpeg`;

    // Handle case where there is only 1 video item
    const videoItems = Array.isArray(currentScene7MediaSetItem.set?.item)
      ? currentScene7MediaSetItem.set?.item
      : [currentScene7MediaSetItem.set?.item];

    // Iterate through the VideoDesc items
    const videos = videoItems.map(
      (videoDesc: Scene7VideoSetItem | undefined) =>
        `${this.environment.thirdParty.scene7?.baseConfig?.baseUrl}${this.environment.thirdParty.scene7?.baseConfig?.videoDirectory}${videoDesc?.i?.n}`,
    );

    // Set the type
    videoSetMediaSetItem.type = 'video';

    // Set the video HTML to the first video in the array
    // Set the track source to use the first video name
    const closedCaptionTag = `<track label="English" kind="captions" srclang="en-us" src="${this.environment.thirdParty.scene7?.baseConfig?.baseUrl}${this.environment.thirdParty.scene7?.baseConfig?.closedCaption}${videoItems[0]?.i?.n}_CC.vtt">`;

    videoSetMediaSetItem.html = `<video crossorigin="anonymous" class="lg-video-object lg-html5" controls preload="auto" autoplay="false"><source src="${videos[0]}" type="video/mp4">${closedCaptionTag}Your browser does not support HTML5 video</video>`;

    return videoSetMediaSetItem;
  }

  /**
   * @description
   * Builds a media set item for image type
   *
   * @param mediaSetItem - Media set item to populate
   * @param scene7ImageName - Image name from Scene7
   * @param fullscreenImageWidths - Widths for image srcset generation
   * @private
   */
  buildImageMediaSetItem(
    mediaSetItem: HmfMediaSetItem,
    scene7ImageName: string | undefined,
    fullscreenImageWidths: number[],
  ): HmfMediaSetItem {
    const imageMediaSetItem = { ...mediaSetItem };

    // Define srcset
    const srcsetArray = fullscreenImageWidths.map((width) => {
      const image = `${this.environment.thirdParty.scene7?.baseConfig?.baseUrl}${this.environment.thirdParty.scene7?.baseConfig?.imageDirectory}${scene7ImageName}?wid=${width}&hei=${width}&fit=constrain&fmt=pjpeg`;
      return `${image} ${width}w`;
    });
    imageMediaSetItem.srcset = srcsetArray.join(', ');

    // Set the type
    imageMediaSetItem.type = 'image';

    // Set the src
    imageMediaSetItem.src = `${this.environment.thirdParty.scene7?.baseConfig?.baseUrl}${this.environment.thirdParty.scene7?.baseConfig?.imageDirectory}${scene7ImageName}?wid=1400&hei=1400&fit=constrain&fmt=pjpeg`;

    return imageMediaSetItem;
  }
}
